import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Injector,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChange,
  SimpleChanges,
  ViewChild
} from '@angular/core'
import {
  AbstractControl,
  ControlValueAccessor,
  ReactiveFormsModule,
  ValidationErrors,
  Validator
} from '@angular/forms'
import { CountryISO, NgxIntlTelInputModule, SearchCountryField } from 'ngx-intl-tel-input'
import { Subject, noop } from 'rxjs'
import { debounceTime, skip, takeUntil, tap } from 'rxjs/operators'
import { NgbDateStruct, NgbDatepickerModule, NgbTooltipModule, NgbDateParserFormatter, NgbDateAdapter } from '@ng-bootstrap/ng-bootstrap'
import { CommonService } from 'src/app/common/services/common.service'
import { CommonModule } from '@angular/common'
import { FieldErrorsComponent } from 'src/app/modules/dashboard/components/field-errors/field-errors.component'
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask'
import { CustomDateParserFormatter, CustomStringDateAdapter } from 'src/app/common/services/formatter.service'

export enum CustomInputType {
  text = 'text',
  number = 'number',
  tel = 'tel',
  date = 'date',
  password = 'password',
  email = 'email',
}

export type Size = 'sm' | 'base' | 'md' | 'lg' | 'xl' | '2xl'

@Component({
    selector: 'app-custom-bx-input',
    templateUrl: './custom-bx-input.component.html',
    styleUrls: ['./custom-bx-input.component.scss'],
    changeDetection: ChangeDetectionStrategy.Default,
    providers: [provideNgxMask(),
        { provide: NgbDateParserFormatter, useClass: CustomDateParserFormatter },
        { provide: NgbDateAdapter, useClass: CustomStringDateAdapter }],
    imports: [CommonModule, ReactiveFormsModule, NgbDatepickerModule, FieldErrorsComponent, NgxIntlTelInputModule, NgbTooltipModule, NgxMaskDirective]
})
export class CustomBxInputComponent
  implements OnInit, AfterViewInit, OnChanges, ControlValueAccessor, Validator, OnDestroy {
  constructor (
    @Inject(Injector) private injector: Injector,
    private elRef: ElementRef,
    private changeDetectionRef: ChangeDetectorRef,
    private commonService: CommonService
  ) { }

  @Input() control: any = null
  @Input() id = ''
  @Input() placeholder = ''
  @Input() type: string = CustomInputType.text
  @Input() disabled = false
  @Input() value = null
  @Input() mask = null
  @Input() tooltip = null
  @Input() isDropSpecialCharacter = false
  @Input() triggerInvalidAnimation = false
  @Input() isPreventPaste = false
  @Input() firstErrorOnly = false
  @Input() isShowError = true
  @Input() isShowRequiredError = true
  @Input() selectedCountryISO: any = ''
  @Input() customErrorMessage = {}
  @Input() format = null
  @Input() label = null
  @Input() size: Size = 'base'
  @Input() showIcon = false
  @Input() data = ''
  @Input() maxlength: number | null = null

  @Output() nextButtonClick = new EventEmitter()
  @Output() focus = new EventEmitter()
  @Output() focusOut = new EventEmitter()
  @Output() countryChanged = new EventEmitter<string>()
  @ViewChild('inputElem') inputElem!: ElementRef
  @ViewChild('inputTelElem', { static: false, read: ElementRef }) inputTelElem!: ElementRef

  isPasswordShow = false
  SearchCountryField = SearchCountryField
  triggerAnimationClass = false
  initialLoad = true
  onDestroy$ = new Subject()
  currentDate!: NgbDateStruct
  dobMinDate!: NgbDateStruct
  expriyMaxDate!: NgbDateStruct
  countryCode!: string

  get errors (): ValidationErrors | null {
    return this.control?.errors
  }

  get formControlValid (): boolean {
    return this.control?.valid
  }

  get formControlInValid (): boolean {
    return this.control?.invalid
  }

  get countryIsInCountryIso (): any {
    if (this.selectedCountryISO) {
      const countryIso = Object.values(CountryISO).find(
        (country) => country === this.selectedCountryISO.toLocaleLowerCase()
      )
      if (countryIso) {
        return countryIso
      }
    }
    return this.selectedCountryISO
  }

  get hasRequiredValidator (): boolean {
    if (this.control) {
      const validators =
        this.control.validator && this.control.validator({} as any)
      return validators && validators['required']
    }
    return false
  }

  public onChange: (value: string) => void = noop
  public onTouch: () => void = noop
  public onValidationChange: () => void = noop

  ngOnInit () {
    this.initDates()
  }

  initDates () {
    const current = new Date()
    this.currentDate = {
      year: current.getFullYear(),
      month: current.getMonth() + 1,
      day: current.getDate()
    }

    this.dobMinDate = {
      year: current.getFullYear() - 100,
      month: current.getMonth() + 1,
      day: current.getDate()
    }

    this.expriyMaxDate = {
      year: current.getFullYear() + 50,
      month: current.getMonth() + 1,
      day: current.getDate()
    }
  }

  ngOnChanges (changes: SimpleChanges): void {
    if (changes['selectedCountryISO'] && changes['selectedCountryISO'].currentValue) {
      this.selectedCountryISO = changes['selectedCountryISO'].currentValue
      this.setCountryCode()
    }

    if (changes['disabled']?.currentValue) {
      setTimeout(() => {
        this.setDisabledState(this.disabled)
      }, 100)
    }

    if (changes['value']?.currentValue) {
      setTimeout(() => {
        this.writeValue(this.value)
      }, 100)
    }
  }

  ngAfterViewInit (): void {
    this.setFormControl()
    this.registerTelInvalidAnimation()
  }

  private registerTelInvalidAnimation (): void {
    setTimeout(() => {
      if (this.control.hasError('required') && this.triggerInvalidAnimation) {
        const elem = this.elRef.nativeElement.querySelector(`#tel-${this.id}`)
        if (!elem) {
          return
        }
        elem.addEventListener('blur', () => {
          this.setInvalidAnimation()
        })
      }
    }, 2000)
  }

  public setInvalidAnimation (): void {
    if (this.control.invalid && this.control.touched) {
      this.triggerAnimationClass = true
      setTimeout(() => {
        this.triggerAnimationClass = false
      }, 1000)
    }
  }

  public togglePassword (): void {
    this.isPasswordShow = !this.isPasswordShow
    this.inputElem.nativeElement.type = this.isPasswordShow
      ? CustomInputType.text
      : CustomInputType.password
    this.setToFocus()
  }

  public preventPaste (event: any): void {
    event.preventDefault()
  }

  public onlyNumbers (event: any): void {
    const eventData = event.clipboardData
      ? event.clipboardData
      : event.dataTransfer
    const pastedData = eventData.getData('Text')
    if (isNaN(pastedData)) {
      event.preventDefault()
    }
  }

  public onFocus (): void {
    this.focus.emit()
  }

  public onFocusout (): void {
    this.focusOut.emit()
  }

  public setToFocus (): void {
    this.inputElem.nativeElement.focus()
  }

  public writeValue (value: any): void {
    this.control?.setValue(value, { emitEvent: false, onlySelf: true })
  }

  public registerOnChange (fn: any): void {
    this.onChange = fn
  }

  public registerOnTouched (fn: any): void {
    this.onTouch = fn
  }

  public registerOnValidatorChange (fn: () => void): void {
    this.onValidationChange = fn
  }

  public setDisabledState (isDisabled: boolean): void {
    if (isDisabled && this.type !== CustomInputType.email) {
      this.control?.disable({ emitEvent: false })
    } else {
      this.control?.enable({ emitEvent: false })
    }
  }

  public setFormControl (): void {
    this.control?.valueChanges
      .pipe(
        skip(1),
        debounceTime(100),
        tap((value: any) => {
          this.onChange(value)
        }),
        takeUntil(this.onDestroy$)
      )
      .subscribe()

    this.control?.statusChanges
      .pipe(
        skip(1),
        tap(() => {
          this.onTouch()
        }),
        takeUntil(this.onDestroy$)
      )
      .subscribe()
  }

  onCountryChange (selectedCountry: any): void {
    if (this.initialLoad) {
      this.initialLoad = false
      return
    }
    this.selectedCountryISO = selectedCountry.iso2
    this.countryChanged.emit(this.selectedCountryISO)
    this.setCountryCode()
  }

  setCountryCode () {
    const container = this.inputTelElem?.nativeElement?.querySelector('.iti__selected-flag')
    if (container) {
      const existingSpan = container.querySelector('.custom-span')
      if (existingSpan) {
        container.removeChild(existingSpan)
      }
      const spanElement = document.createElement('span')
      spanElement.textContent = this.selectedCountryISO?.toUpperCase()
      spanElement.classList.add('custom-span')
      container.appendChild(spanElement)
    }
  }

  public validate (control: AbstractControl): ValidationErrors | null {
    if (this.control?.invalid) {
      return { invalid: true }
    } else {
      return null
    }
  }

  private buildDateValue(existing: string, chars: string): string {
    let value = existing;
    for (const char of chars) {
      if (!/[0-9/]/.test(char)) { continue; }
      if (value.length >= 10) { break; }
      if (char === '/') {
        if ((value.match(/\//g) || []).length >= 2 || value.length === 0 || value[value.length - 1] === '/') {
          continue;
        }
        value += char;
        continue;
      }
      value = this.appendDigit(value, char);
    }
    return value.substring(0, 10);
  }

  private appendDigit(value: string, char: string): string {
    const parts = value.split('/');
    const sectionIndex = parts.length - 1;
    const maxLen = [2, 2, 4][sectionIndex];

    if (parts[sectionIndex].length >= maxLen) {
      if ((value.match(/\//g) || []).length < 2) {
        value += '/' + char;
      }
    } else {
      value += char;
    }
    return value;
  }

  onDatePaste(event: ClipboardEvent): void {
    event.preventDefault();
    const paste = event.clipboardData?.getData('text') || '';
    const input = event.target as HTMLInputElement;
    if (/[^0-9/]/.test(paste)) { return; }

    const pos = input.selectionStart || 0;
    const endPos = input.selectionEnd || pos;

    const before = input.value.substring(0, pos);
    const after = input.value.substring(endPos);

    const built = this.buildDateValue(before, paste + after);
    const cursorPos = this.buildDateValue(before, paste).length;

    input.value = built;
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.setSelectionRange(cursorPos, cursorPos);
  }

  private isDateKeyPressInput = false;

  onDateKeyPress(event: KeyboardEvent): void {
    const input = event.target as HTMLInputElement;
    const pos = input.selectionStart || 0;
    const { key } = event;

    if (!/[0-9/]/.test(key)) { event.preventDefault(); return; }
    if (input.value.length >= 10 && pos === input.selectionEnd) { event.preventDefault(); return; }

    event.preventDefault();
    const before = input.value.substring(0, pos);
    const after = input.value.substring(input.selectionEnd || pos);
    const built = this.buildDateValue(before, key + after);
    input.value = built;
    input.setSelectionRange(this.buildDateValue(before, key).length, this.buildDateValue(before, key).length);
    this.isDateKeyPressInput = true;
    input.dispatchEvent(new Event('input', { bubbles: true }));
    this.isDateKeyPressInput = false;
  }

  onDateInput(event: Event): void {
    if (this.isDateKeyPressInput) { return; }
    const input = event.target as HTMLInputElement;
    const raw = input.value;

    // If it contains invalid characters, strip only those characters instead of
    // wiping the whole field, then let the datepicker directive read the value.
    if (/[^0-9/]/.test(raw)) {
      const cursorPos = input.selectionStart || 0;
      const cleaned = raw.replace(/[^0-9/]/g, '');
      const removed = raw.length - cleaned.length;
      input.value = cleaned;
      input.setSelectionRange(Math.max(0, cursorPos - removed), Math.max(0, cursorPos - removed));
      input.dispatchEvent(new Event('input', { bubbles: true }));
      return;
    }

    // Allow partial values during editing (typing/deletion) as-is. The ngb
    // datepicker directive's own (input) listener propagates the value to the
    // form control, so we must NOT call setValue here (that would trigger the
    // directive's writeValue and blank a partial value).
    const parts = raw.split('/');
    const isValidStructure = parts.length <= 3 &&
      (parts[0]?.length || 0) <= 2 &&
      (parts[1]?.length ?? 0) <= 2 &&
      (parts[2]?.length ?? 0) <= 4 &&
      !/\/\//.test(raw) &&
      raw.length <= 10;

    if (isValidStructure) {
      return;
    }

    // Malformed structure (e.g. consecutive slashes / overlength): rebuild the
    // masked string in the DOM and re-dispatch so the directive picks it up.
    const sanitized = this.buildDateValue('', raw);
    if (sanitized !== raw) {
      const cursorPos = input.selectionStart || sanitized.length;
      input.value = sanitized;
      input.setSelectionRange(Math.min(cursorPos, sanitized.length), Math.min(cursorPos, sanitized.length));
      input.dispatchEvent(new Event('input', { bubbles: true }));
    }
  }

  ngOnDestroy (): void {
    this.onDestroy$.next(true)
  }
}
